import { Box } from "@mui/material";
import Link from "next/link";
import type { ReactNode } from "react";
import { SiteRoot } from "src/domains/shared/components/Home/SiteShell/SiteRoot";
import {
  SITE_AUTH_COLOR,
  SITE_COLOR,
  SITE_MEDIA,
  SITE_TOUCH_TARGET,
} from "src/domains/shared/constants/styles/site";

/** 카드 위아래 기본 여백 — 위쪽 워드마크를 비켜 서는 거리. 카드가 화면보다 길면 이 여백을 두고 페이지가 내려간다 */
const EDGE = "64px";

/**
 * 카드 가로 위치: 넓은 화면은 가운데에서 오른쪽으로 min(22vw, 316px) 옮긴 곳이 카드 가운데(시안 `.col`).
 * 세로는 화면 46%(휴대폰 43%)가 카드 가운데 — 시안은 절대 위치라 카드가 화면보다 길면 잘렸으므로, 문서 흐름 안에서 아래 여백을
 * 위보다 화면 높이의 8%(14%) 더 두어 같은 자리에 세우고, 길면 스크롤된다.
 */
const SHELL_STYLES = {
  root: {
    position: "relative",
    minHeight: "100vh",
    overflow: "hidden",
    background: SITE_AUTH_COLOR.backdrop,
    color: SITE_COLOR.heading,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    paddingTop: EDGE,
    paddingBottom: `calc(${EDGE} + 8vh)`,
    "@supports (height: 100svh)": {
      minHeight: "100svh",
      paddingBottom: `calc(${EDGE} + 8svh)`,
    },
    [SITE_MEDIA.tablet]: {
      paddingBottom: EDGE,
      "@supports (height: 100svh)": { paddingBottom: EDGE },
    },
    [SITE_MEDIA.phone]: {
      paddingBottom: `calc(${EDGE} + 14vh)`,
      "@supports (height: 100svh)": { paddingBottom: `calc(${EDGE} + 14svh)` },
    },
    // 가로로 둔 터치 화면: 두 칸 카드를 화면 높이 가운데에 — 워드마크는 왼쪽 칸 위 빈자리에 걸린다
    [SITE_MEDIA.landscapeTouch]: {
      paddingTop: "16px",
      paddingBottom: "16px",
      "@supports (height: 100svh)": { paddingBottom: "16px" },
    },
  },
  // 왼쪽 아래 모서리에 걸친 큰 투명 앱 아이콘 타일(장식)
  tile: {
    position: "absolute",
    left: "calc(-1 * min(260px,18vw))",
    bottom: "calc(-1 * min(320px,22vw))",
    width: "min(980px,68vw)",
    height: "min(980px,68vw)",
    borderRadius: "26%",
    transform: "rotate(-10deg)",
    opacity: 0.6,
    background: "linear-gradient(150deg,#FFFFFF 0%,#F5F7FA 55%,#E6EAF0 100%)",
    boxShadow:
      "0 60px 120px -40px rgba(29,116,255,.28),0 0 0 1px rgba(29,116,255,.08),inset 0 3px 0 #fff",
    pointerEvents: "none",
    "&::before": {
      content: '""',
      position: "absolute",
      inset: 0,
      borderRadius: "inherit",
      background:
        "linear-gradient(180deg,rgba(255,255,255,.8),rgba(255,255,255,0) 60%)",
    },
    "& svg": {
      position: "absolute",
      left: "18%",
      top: "17%",
      width: "64%",
      height: "64%",
      opacity: 0.7,
    },
    [SITE_MEDIA.tablet]: {
      left: "-200px",
      bottom: "-260px",
      width: "620px",
      height: "620px",
      opacity: 0.5,
    },
    // 가로로 둔 터치 화면: 왼쪽 칸 제목·안내 아래로 비켜 모서리에만 걸친다
    [SITE_MEDIA.landscapeTouch]: {
      left: "-200px",
      bottom: "-330px",
      width: "460px",
      height: "460px",
    },
  },
  wordmark: {
    ...SITE_TOUCH_TARGET,
    position: "absolute",
    left: "clamp(20px,3.9vw,56px)",
    top: "clamp(20px,3vw,44px)",
    zIndex: 2,
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "18px",
    fontWeight: 800,
    letterSpacing: "-.02em",
    color: SITE_COLOR.heading,
    "& svg": { width: "22px", height: "22px" },
  },
  column: {
    position: "relative",
    zIndex: 2,
    width: "min(400px,calc(100% - 48px))",
    marginLeft: "calc(50% + min(22vw,316px) - min(200px,50% - 24px))",
    [SITE_MEDIA.tablet]: { marginInline: "auto" },
    // 가로로 둔 터치 화면: 워드마크와 같은 왼쪽 여백에서 시작하는 두 칸 폭
    [SITE_MEDIA.landscapeTouch]: {
      width: "min(820px,calc(100% - 2 * clamp(20px,3.9vw,56px)))",
      marginInline: "auto",
    },
  },
} as const;

const MARK_PATHS = (
  <>
    <path
      d="M25.3198 3.86572L3.97754 12.0342L22.5569 26.6094L25.3198 3.86572Z"
      fill={SITE_COLOR.gold}
    />
    <path
      d="M28.0226 10.9556L10.9248 17.4846L25.8092 29.1345L28.0226 10.9556Z"
      fill={SITE_COLOR.navy}
    />
  </>
);

/**
 * 로그인·회원가입·비밀번호 변경 화면의 틀(marketing-auth-pages, 확정 시안 G9-6): 연회색 바탕, 왼쪽 아래 큰 앱 아이콘 타일(장식),
 * 왼쪽 위 DroneSquare 워드마크(홈 링크), 오른쪽 입력 카드. 사이트 머리말·푸터는 두지 않는다. 가로로 둔 터치 화면은 카드가 두 칸으로 넓어진다.
 */
export const LoginBackground = ({ children }: { children: ReactNode }) => {
  return (
    <SiteRoot sx={SHELL_STYLES.root}>
      <Box aria-hidden="true" sx={SHELL_STYLES.tile}>
        <svg viewBox="2 2 28 29">{MARK_PATHS}</svg>
      </Box>
      <Box component={Link} href="/" sx={SHELL_STYLES.wordmark}>
        <svg viewBox="2 2 28 29" aria-hidden="true">
          {MARK_PATHS}
        </svg>
        DroneSquare
      </Box>
      <Box component="main" id="main" sx={SHELL_STYLES.column}>
        {children}
      </Box>
    </SiteRoot>
  );
};
